import firebase from 'firebase/compat/app'
import * as lodash from 'lodash'
import moment from 'moment'

export enum ParticipantType {
  USER = 1,
  CONTACT = 2,
}

export enum ParticipantStatus {
  GROUP_CHAT_CONTACT_DELETED = 1,
  GROUP_CHAT_CONTACT_UPDATED = 2,
  INTERNAL_CHAT_USER_REMOVED = 3, // removal of user is not part of phase 1
  GROUP_CHAT_CONTACT_ACTIVE = 4,
}

export const ParticipantStatusLabels: { [key in ParticipantStatus]: string } = {
  [ParticipantStatus.GROUP_CHAT_CONTACT_DELETED]: 'deleted',
  [ParticipantStatus.GROUP_CHAT_CONTACT_UPDATED]: 'updated',
  [ParticipantStatus.INTERNAL_CHAT_USER_REMOVED]: 'user removed',
  [ParticipantStatus.GROUP_CHAT_CONTACT_ACTIVE]: 'active',
}

export enum ConversationParticipantTypes {
  INTERNAL_CHAT = 1,
  GROUP_CHAT = 2,
}

export interface Participant {
  id: string // immutable
  type: ParticipantType // immutable
  alt_id?: string //participant_sid from twilio in GHL DB // immutable -- group chat specific
  status?: ParticipantStatus // mutable
  unread_count?: number //mutable -- internal chat specific
  phone?: string
  primaryPhone?: boolean // To keep record of if number added, is it primary numnber.
  fullName?: string // Full name of the contact
  email?: string // Email of the contact
  profilePhoto?: string // Profile photo of the contact
  dnd?: boolean // Do not disturb status of the contact
  assignedTo?: string // Assigned to user id
}

export default class ConversationParticipants {
  protected static collectionName = 'conversation_participants'
  status: ParticipantStatus

  private _data: firebase.firestore.DocumentData
  private _id: string
  private _ref: firebase.firestore.DocumentReference
  private _snapshot: firebase.firestore.DocumentSnapshot
  public static collectionRef() {
    return firebase.firestore().collection(this.collectionName)
  }
  constructor(snapshot?: firebase.firestore.DocumentSnapshot | { [key: string]: any }) {
    if (
      snapshot instanceof firebase.firestore.DocumentSnapshot ||
      snapshot instanceof firebase.firestore.QueryDocumentSnapshot
    ) {
      this._id = snapshot.id
      this._data = snapshot.data() || {}
      this._ref = snapshot.ref
      this._snapshot = snapshot
    } else if (snapshot) {
      this._id = snapshot.id
      this._data = snapshot
      this._ref = ConversationParticipants.collectionRef().doc(snapshot.id)
      this._data.date_added = new firebase.firestore.Timestamp(Math.floor(snapshot.date_added / 1000), 0)
      this._data.date_updated = new firebase.firestore.Timestamp(Math.floor(snapshot.date_updated / 1000), 0)
    } else {
      this._ref = ConversationParticipants.collectionRef().doc()
      this._id = this._ref.id
      this._data = {}
      this.deleted = false
      this.dateAdded = moment().utc()
    }
  }

  // functions
  public static getByLocationId(locationId: string) {
    return ConversationParticipants.collectionRef().where('location_id', '==', locationId)
  }

  public static async getById(id: string): Promise<ConversationParticipants> {
    return new Promise<ConversationParticipants>((resolve, reject) => {
      ConversationParticipants.collectionRef()
        .doc(id)
        .get()
        .then((snapshot: firebase.firestore.DocumentSnapshot) => {
          if (!snapshot.exists) return resolve()
          if (snapshot.data().deleted) return resolve()
          resolve(new ConversationParticipants(snapshot))
        })
        .catch((err) => {
          console.error(err)
          reject(err)
        })
    })
  }

  // Getters and Setters

  get snapshot(): firebase.firestore.DocumentSnapshot {
    return this._snapshot
  }

  get id(): string {
    return this._id
  }

  get participantHash(): string {
    return this._data.participant_hash
  }

  set participantHash(participantHash: string) {
    this._data.participant_hash = participantHash
  }

  get dateUpdated(): moment.Moment {
    return this._data.date_updated ? moment(this._data.date_updated.toMillis()) : undefined
  }

  set dateUpdated(dateUpdated: moment.Moment) {
    this._data.date_updated = dateUpdated ? firebase.firestore.Timestamp.fromMillis(dateUpdated.valueOf()) : undefined
  }

  get deleted(): boolean {
    return this._data.deleted
  }

  set deleted(deleted: boolean) {
    this._data.deleted = deleted
  }

  get conversationId(): string {
    return this._data.conversation_id
  }

  set conversationId(conversationId: string) {
    this._data.conversation_id = conversationId
  }

  get userId(): string {
    return this._data.user_id
  }

  set userId(userId: string) {
    this._data.user_id = userId
  }

  get locationId(): string {
    return this._data.location_id
  }

  set locationId(locationId: string) {
    this._data.location_id = locationId
  }

  get participants(): Participant[] {
    return lodash.isEmpty(this._data.participants) ? [] : this._data.participants
  }

  set participants(participants: Participant[]) {
    this._data.participants = participants
  }

  get fromNumber(): string {
    return this._data.from_number
  }

  set fromNumber(fromNumber: string) {
    this._data.from_number = fromNumber
  }

  get participantIds(): string[] {
    return this._data.participants_ids
  }

  set participantIds(participantIds: string[]) {
    this._data.participants_ids = participantIds
  }

  get type(): number {
    return this._data.type
  }

  set type(type: number) {
    this._data.type = type
  }

  get dateAdded(): moment.Moment {
    return this._data.date_added ? moment(this._data.date_added.toMillis()) : undefined
  }

  set dateAdded(dateAdded: moment.Moment) {
    this._data.date_added = dateAdded ? firebase.firestore.Timestamp.fromMillis(dateAdded.valueOf()) : undefined
  }
}
